/**
 * AuthProvider
 * ============
 *
 * Wraps the React tree in a context that mirrors the current Supabase
 * session. Everywhere else in the app uses ``useAuth()`` (below) — no
 * direct ``supabase.auth.getSession()`` calls scattered around.
 *
 * Why a context (not just a hook around supabase-js):
 *   - We need one shared subscription to ``onAuthStateChange``; mounting
 *     it per consumer would multiply listeners.
 *   - The axios interceptor reads the access token via a module-level
 *     getter (see ``../api/client.ts``); the provider keeps that getter
 *     wired to the latest session.
 *
 * Lifecycle:
 *   1. Mount -> ``getSession()`` (cache hit on refresh) -> set state.
 *   2. ``onAuthStateChange`` fires for every login / logout / refresh.
 *   3. Unmount -> unsubscribe.
 */
import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import type { Session, User as SupabaseUser } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "./supabase";
import { getMe, setAccessTokenGetter } from "../api/client";

interface AuthContextValue {
  session: Session | null;
  user: SupabaseUser | null;
  loading: boolean;           // true until the first getSession() resolves
  configured: boolean;        // false if VITE_SUPABASE_* env is missing
  // Phase 6: backend-attested admin flag. Read via the typed useAdmin()
  // helper below; gates admin-only UI (Upload form catalog checkbox,
  // Catalog tab Delete / Edit buttons). False during loading + for any
  // user the backend hasn't tagged via PBMC_ADMIN_EMAILS.
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);


export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Wire the API client's token getter once at mount so every axios
  // request reads the *current* access token (state-after-refresh)
  // instead of a stale closure capture.
  useEffect(() => {
    setAccessTokenGetter(() => session?.access_token ?? null);
  }, [session]);

  // Resolve admin status from the backend on session change AND once on
  // mount. The mount-time call covers the API-key auth path (no Supabase
  // session ever fires, but ?api_key= / X-API-Key still authenticates
  // /api/me). A failed call -> isAdmin stays false; the admin-gated UI
  // just stays hidden, which is the safe default.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      getMe()
        .then((me) => { if (!cancelled) setIsAdmin(me.is_admin); })
        .catch(() => { if (!cancelled) setIsAdmin(false); });
    });
    return () => { cancelled = true; };
  }, [session]);

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured: supabaseConfigured,
      isAdmin,
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [session, loading, isAdmin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/** Convenience helper for admin-gated UI. Returns false during the
 *  brief window between sign-in and the /api/me round-trip resolving. */
export function useAdmin(): boolean {
  return useAuth().isAdmin;
}
